import { OrgUserRoles } from '@zeta-types';

export const USER_PARTITIONS = {
  FPO: 'FPO',
  FBS: 'FBS',
} as const;

export const userRoleOptions = [
  {
    label: 'Super Administrator',
    value: 'Super Administrator',
    id: '1',
    name: 'Super Administrator',
  },
  {
    label: 'Financial Advisor',
    value: 'Financial Advisor',
    id: '2',
    name: 'Financial Advisor',
  },
  {
    label: 'Paraplanner',
    value: 'Paraplanner',
    id: '3',
    name: 'Paraplanner',
  },
  // {
  //   label: 'Administrator',
  //   value: 'Administrator',
  //   id: '4',
  //   name: 'Administrator',
  // },
  {
    label: 'Executive Officer',
    value: 'Executive Officer',
    id: '5',
    name: 'Executive Officer',
  },
  {
    label: 'Compliance Officer',
    value: 'Compliance Officer',
    id: '6',
    name: 'Compliance Officer',
  },
  {
    label: 'Relationship Manager',
    value: 'Relationship Manager',
    id: '7',
    name: 'Relationship Manager',
  },
];

export const FBSUserRoleOptions = [
  {
    label: 'FBS Admin',
    value: 'FBS Admin',
    id: '1',
    name: 'FBS Admin',
  },
  {
    label: 'FBS Member',
    value: 'FBS Member',
    id: '2',
    name: 'FBS Member',
  },
  {
    label: 'Business Architect',
    value: 'Business Architect',
    id: '3',
    name: 'Business Architect',
  },
];

export const FBS_ROLE_NAMES = FBSUserRoleOptions.map((role) => role.name);
export const FPO_ROLE_NAMES = userRoleOptions.map((role) => role.name);

export const ALL_USER_ROLE_OPTIONS = [...userRoleOptions, ...FBSUserRoleOptions];

export const userStatusOptions = [
  {
    id: '1',
    label: 'Active',
    value: 'Active',
  },
  {
    id: '2',
    label: 'Deactivated',
    value: 'Deactivated',
  },
  {
    id: '3',
    label: 'Pending',
    value: 'Pending',
  },
  {
    id: '4',
    label: 'Inactive',
    value: 'Inactive',
  },
];

export const userAccessOptions = [
  {
    id: '1',
    label: 'Advisor Dashboard',
    name: 'AdvisorDashboard',
  },
  {
    id: '2',
    label: 'Advisor Notification',
    name: 'AdvisorNotification',
  },
  {
    id: '3',
    label: 'Advisor MPS',
    name: 'AdvisorMPS',
  },
  {
    id: '4',
    label: 'AI Interaction',
    name: 'AIInteraction',
  },
  {
    id: '5',
    label: 'Advisor-Client',
    name: 'Advisor-Client',
  },
  {
    id: '6',
    label: 'Advisor Task Workflow',
    name: 'AdvisorTaskWorkflow',
  },
  {
    id: '7',
    label: 'Advisor Meeting',
    name: 'AdvisorMeeting',
  },
  {
    id: '8',
    label: 'Advisor Org Config',
    name: 'AdvisorOrgConfig',
  },
];

const roleNameToBackendValue: Record<string, string> = {
  'Super Administrator': OrgUserRoles.super_administrator,
  'Financial Advisor': OrgUserRoles.financial_advisor,
  Paraplanner: OrgUserRoles.para_planner,
  Administrator: OrgUserRoles.administrator,
  'Executive Officer': OrgUserRoles.executive_officer,
  'Compliance Officer': OrgUserRoles.compliance_officer,
  'Relationship Manager': OrgUserRoles.relationship_manager,
  'FBS Admin': OrgUserRoles.fbs_administrator,
  'FBS Member': OrgUserRoles.fbs_member,
  'Business Architect': OrgUserRoles.business_architect,
};

const normalizeRoleName = (roleName: string) => roleName.trim();

export const mapRoleNamesToBackendRoles = (roles: string[]) =>
  roles.map((role) => roleNameToBackendValue[normalizeRoleName(role)] || normalizeRoleName(role));

const FPO_BACKEND_ROLE_VALUES = [
  OrgUserRoles.super_administrator,
  OrgUserRoles.financial_advisor,
  OrgUserRoles.para_planner,
  OrgUserRoles.administrator,
  OrgUserRoles.executive_officer,
  OrgUserRoles.compliance_officer,
  OrgUserRoles.relationship_manager,
];

const FBS_BACKEND_ROLE_VALUES = [OrgUserRoles.fbs_administrator, OrgUserRoles.fbs_member, OrgUserRoles.business_architect];

const hasAnyRole = (roles: string[] | null | undefined, allowedRoles: string[]) =>
  roles?.some((role) => allowedRoles.includes(normalizeRoleName(role))) ?? false;

export const hasFinancialAdvisorRole = (orgRoles: string[] | null | undefined) =>
  hasAnyRole(orgRoles, ['Financial Advisor', OrgUserRoles.financial_advisor]);

export const hasFbsAdminRole = (orgRoles: string[] | null | undefined) => hasAnyRole(orgRoles, ['FBS Admin', OrgUserRoles.fbs_administrator]);

export const hasFbsMemberRole = (orgRoles: string[] | null | undefined) => hasAnyRole(orgRoles, ['FBS Member', OrgUserRoles.fbs_member]);

export const hasFbsRole = (orgRoles: string[] | null | undefined) => hasAnyRole(orgRoles, [...FBS_ROLE_NAMES, ...FBS_BACKEND_ROLE_VALUES]);

export const hasFpoRole = (orgRoles: string[] | null | undefined) =>
  hasAnyRole(orgRoles, [...FPO_ROLE_NAMES, ...FPO_BACKEND_ROLE_VALUES]);

export const canAccessMeetingConfiguration = (orgRoles: string[] | null | undefined) =>
  hasFinancialAdvisorRole(orgRoles) || hasFbsAdminRole(orgRoles) || hasFbsMemberRole(orgRoles);

export const getPartitionForRoles = (roles: string[]) => {
  if (hasFpoRole(roles)) return 'FPO';
  if (hasFbsRole(roles)) return 'FBS';
  return null;
};

export const isFbsOnlyUser = (roles: string[] | null | undefined) => !!roles?.length && roles.every((role) => FBS_ROLE_NAMES.includes(normalizeRoleName(role)));

export const hasSuperAdminRole = (orgRoles: string[] | null | undefined) =>
  hasAnyRole(orgRoles, ['Super Administrator', OrgUserRoles.super_administrator]);

export const hasPlatformSuperAdminRole = (userRole: string | null | undefined, orgRoles: string[] | null | undefined) => userRole === 'Admin' && !orgRoles?.length;

export const hasSuperAdminPrivileges = (orgRoles: string[] | null | undefined, userRole?: string | null) =>
  hasSuperAdminRole(orgRoles) || hasPlatformSuperAdminRole(userRole, orgRoles);

export const canManageFbsRoles = (orgRoles: string[] | null | undefined, userRole?: string | null) =>
  hasSuperAdminPrivileges(orgRoles, userRole) || hasFbsAdminRole(orgRoles);

export const canManageFpoRoles = (orgRoles: string[] | null | undefined, userRole?: string | null) =>
  !hasFbsAdminRole(orgRoles) || hasSuperAdminPrivileges(orgRoles, userRole);

export const getAllowedUserRoleOptions = (orgRoles: string[] | null | undefined, _userRole?: string | null) => {
  void _userRole;

  if (hasFbsAdminRole(orgRoles)) {
    return FBSUserRoleOptions;
  }

  return ALL_USER_ROLE_OPTIONS;
};
